import {
  ArrowLeft,
  Building2,
  ExternalLink,
  MapPin,
  Phone,
  RefreshCw,
  Search,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  FacilitiesResult,
  geocodeDestination,
  getHealthcareFacilities,
  HealthcareFacility,
} from "../../services/api/travelHealth";
import { getTravel, Travel } from "../../services/api/travels";
import {
  useLocaleFormat,
  type LocaleFormat,
} from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";

const typeConfig: Record<
  string,
  { label: string; color: string; bg: string; icon: string }
> = {
  hospital: {
    label: "Hospital",
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
    icon: "🏥",
  },
  pharmacy: {
    label: "Pharmacy",
    color: "text-green-700",
    bg: "bg-green-50 border-green-200",
    icon: "💊",
  },
  clinic: {
    label: "Clinic",
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
    icon: "🏨",
  },
  doctors: {
    label: "Doctor",
    color: "text-purple-700",
    bg: "bg-purple-50 border-purple-200",
    icon: "👨‍⚕️",
  },
  dentist: {
    label: "Dentist",
    color: "text-sky-700",
    bg: "bg-sky-50 border-sky-200",
    icon: "🦷",
  },
};

const ACTIVE_TYPES = ["hospital", "pharmacy", "clinic"];

/** Metres from the API, shown in m/km, or ft/mi for English. */
const formatDistance = (m: number, fmt: LocaleFormat) => {
  if (fmt.unitSystem === "imperial") {
    // Below a tenth of a mile, feet read better than "0.0 mi".
    if (m < 161) return `${fmt.number(Math.round(m * 3.28084), 0)} ft`;
    return fmt.quantity("distance", m / 1000);
  }
  if (m < 1000) return `${Math.round(m)} m`;
  return fmt.quantity("distance", m / 1000);
};

export const HealthcareFacilitiesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();

  const [travel, setTravel] = useState<Travel | null>(null);
  const [result, setResult] = useState<FacilitiesResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(ACTIVE_TYPES);
  // The radius is chosen in the display unit (mi for English) and sent in km.
  const [radius, setRadius] = useState(5);
  const radiusKm = fmt.fromDisplay("distance", radius);
  const [filterText, setFilterText] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(
    null,
  );

  const loadTravel = useCallback(async (travelId: string) => {
    try {
      setLoading(true);
      const data = await getTravel(travelId);
      setTravel(data);
      if (data.destination && data.country) {
        await geocodeAndLoad(data.destination, data.country);
      }
    } catch {
      setError("Failed to load travel.");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id) void loadTravel(id as string);
  }, [id, loadTravel]);

  const geocodeAndLoad = async (destination: string, country: string) => {
    try {
      setLoadingFacilities(true);
      setError(null);
      const geo = await geocodeDestination(`${destination}, ${country}`);
      setCoords({ lat: geo.lat, lon: geo.lon });
      const data = await getHealthcareFacilities(
        geo.lat,
        geo.lon,
        Math.round(radiusKm * 1000),
        selectedTypes,
      );
      setResult(data);
    } catch (err: unknown) {
      const apiErr = err as { response?: { status?: number } };
      setError(
        apiErr?.response?.status === 404
          ? `Location not found: ${destination}, ${country}`
          : "Could not load healthcare facilities.",
      );
    } finally {
      setLoadingFacilities(false);
    }
  };

  const reload = async () => {
    if (!coords) return;
    try {
      setLoadingFacilities(true);
      setError(null);
      const data = await getHealthcareFacilities(
        coords.lat,
        coords.lon,
        Math.round(radiusKm * 1000),
        selectedTypes,
      );
      setResult(data);
    } catch {
      setError("Could not load healthcare facilities.");
    } finally {
      setLoadingFacilities(false);
    }
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const filteredFacilities = (result?.facilities ?? []).filter((f) => {
    const matchesType = selectedTypes.includes(f.type);
    const matchesText =
      filterText.trim() === "" ||
      f.name.toLowerCase().includes(filterText.toLowerCase()) ||
      (f.address ?? "").toLowerCase().includes(filterText.toLowerCase());
    return matchesType && matchesText;
  });

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/traveler/travels/${id ?? ""}`)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <PageBanner
          icon={Building2}
          title={t.healthcareFacilities ?? "Healthcare Facilities"}
          description={travel ? `${travel.destination}, ${travel.country}` : ""}
        />
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow p-5 space-y-4">
        {/* Type Filter */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Facility types:
          </p>
          <div className="flex flex-wrap gap-2">
            {ACTIVE_TYPES.map((type) => {
              const cfg = typeConfig[type] ?? {
                label: type,
                icon: "🏥",
                bg: "bg-gray-50 border-gray-200",
                color: "text-gray-700",
              };
              const active = selectedTypes.includes(type);
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                    active
                      ? `${cfg.bg} ${cfg.color} ring-2 ring-offset-1 ring-current`
                      : "bg-white border-gray-200 text-gray-400"
                  }`}
                >
                  <span>{cfg.icon}</span>
                  {t[
                    `nearby${type.charAt(0).toUpperCase() + type.slice(1)}s` as keyof typeof t
                  ] ?? cfg.label}
                  {result?.counts?.[type] != null && (
                    <span className="text-xs opacity-70">
                      ({result.counts[type]})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Radius + Search */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label htmlFor="radius-select" className="text-sm text-gray-600">
              {t.fmtRadiusLabel}
            </label>
            <select
              id="radius-select"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1"
            >
              {[1, 2, 5, 10, 20].map((r) => (
                <option key={r} value={r}>
                  {r} {fmt.unit("distance")}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 relative min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter by name or address…"
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={reload}
            disabled={loadingFacilities}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw
              className={`h-4 w-4 ${loadingFacilities ? "animate-spin" : ""}`}
            />
            Search
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
          {error}
        </div>
      )}

      {loadingFacilities && (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      )}

      {/* Results */}
      {!loadingFacilities && result && (
        <>
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {t.fmtFacilitiesWithin
              .replace("{count}", String(filteredFacilities.length))
              .replace("{distance}", `${radius} ${fmt.unit("distance")}`)
              .replace("{place}", travel?.destination ?? "")}
          </div>

          {filteredFacilities.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
              {t.noFacilitiesFound ?? "No facilities found in this area"}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredFacilities.map((facility) => (
                <FacilityCard key={facility.id} facility={facility} />
              ))}
            </div>
          )}

          <div className="text-xs text-gray-400 text-center pb-4">
            Data from OpenStreetMap contributors ·{" "}
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              ODbL License
            </a>
          </div>
        </>
      )}
    </div>
  );
};

const DEFAULT_CFG = {
  label: "Facility",
  color: "text-gray-700",
  bg: "bg-gray-50 border-gray-200",
  icon: "🏥",
};

const FacilityCard: React.FC<{ facility: HealthcareFacility }> = ({
  facility,
}) => {
  const fmt = useLocaleFormat();
  const cfg = typeConfig[facility.type] ?? DEFAULT_CFG;

  return (
    <div
      className={`rounded-xl border p-4 ${cfg.bg} hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl shrink-0">{cfg.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 text-sm leading-tight">
              {facility.name}
            </h3>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 bg-white ${cfg.color}`}
            >
              {formatDistance(facility.distanceM, fmt)}
            </span>
          </div>

          <p className={`text-xs font-medium mt-0.5 capitalize ${cfg.color}`}>
            {facility.type}
          </p>

          {facility.address && (
            <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
              <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
              {facility.address}
            </p>
          )}

          {facility.openingHours && (
            <p className="text-xs text-gray-500 mt-1">
              🕐 {facility.openingHours}
            </p>
          )}

          {facility.emergency === "yes" && (
            <span className="inline-block mt-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
              Emergency
            </span>
          )}

          <div className="flex items-center gap-3 mt-2">
            {facility.phone && (
              <a
                href={`tel:${facility.phone}`}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <Phone className="h-3 w-3" />
                {facility.phone}
              </a>
            )}
            {facility.website && (
              <a
                href={facility.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-3 w-3" />
                Website
              </a>
            )}
            <a
              href={`https://www.openstreetmap.org/?mlat=${facility.lat}&mlon=${facility.lon}&zoom=17`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 ml-auto"
            >
              <MapPin className="h-3 w-3" />
              Map
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
